import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * ContactsLocation — Google Map embed + address / hours block.
 *
 * The map iframe is a plain Google Maps embed URL, so no API key is
 * needed and no client-side JS is required to render it.
 */
export default function ContactsLocation() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const mapRef = useEnterAnimation<HTMLDivElement>({ delay: 260 });
  const detailsRef = useEnterAnimation<HTMLDivElement>({ delay: 420 });

  return (
    <section
      id="contacts-location"
      className="relative w-full bg-background-100"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Contacts / 04
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 04 · Where we are
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex items-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto">
          <div ref={eyebrowRef} className="mb-8 flex items-center gap-3">
            <span
              className="font-heading"
              style={{
                fontSize: "clamp(2.75rem, 4vw, 4rem)",
                fontWeight: 300,
                letterSpacing: "-0.03em",
                color: "oklch(var(--foreground-950) / 0.35)",
                lineHeight: 1,
                fontVariationSettings: "'opsz' 144",
              }}
            >
              04
            </span>
            <span className="w-12 h-[1px] bg-foreground-950/25"></span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
              Find the studio
            </span>
          </div>

          <h2
            ref={titleRef}
            className="mb-14 max-w-[900px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2.5rem, 5vw, 5rem)",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              lineHeight: 1.05,
              color: "oklch(var(--foreground-950))",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            A quiet street in
            <br />
            <span className="italic text-foreground-950/85">
              central Lisbon.
            </span>
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
            {/* Map */}
            <div ref={mapRef} className="lg:col-span-8">
              <div className="relative w-full overflow-hidden rounded-2xl border border-foreground-950/10 bg-background-50">
                <div className="relative w-full" style={{ aspectRatio: "16 / 11" }}>
                  <iframe
                    title="Serene studio location — Lisbon"
                    src="https://www.google.com/maps?q=Rua%20Garrett%2C%20Lisbon%2C%20Portugal&t=&z=15&ie=UTF8&iwloc=&output=embed"
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Details column */}
            <div ref={detailsRef} className="lg:col-span-4 flex flex-col gap-6">
              {/* Address */}
              <div className="rounded-2xl border border-foreground-950/10 bg-background-50 p-6 md:p-7">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <span
                    className="uppercase font-medium text-foreground-950/60"
                    style={{ fontSize: "10px", letterSpacing: "0.3em" }}
                  >
                    The Studio
                  </span>
                </div>
                <div
                  className="mb-2"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(1.15rem, 1.35vw, 1.4rem)",
                    fontWeight: 400,
                    lineHeight: 1.35,
                    color: "oklch(var(--foreground-950))",
                    letterSpacing: "-0.005em",
                  }}
                >
                  Rua Garrett 22, 3rd floor
                  <br />
                  Chiado, 1200-203 Lisbon
                  <br />
                  Portugal
                </div>
                <a
                  href="https://maps.google.com/?q=Rua+Garrett,+Lisbon,+Portugal"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase font-medium text-foreground-950/70 hover:text-foreground-950 transition-colors duration-200 mt-2"
                >
                  Open in Google Maps
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </a>
              </div>

              {/* Hours */}
              <div className="rounded-2xl border border-foreground-950/10 bg-background-50 p-6 md:p-7">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <span
                    className="uppercase font-medium text-foreground-950/60"
                    style={{ fontSize: "10px", letterSpacing: "0.3em" }}
                  >
                    Studio Hours
                  </span>
                </div>
                <ul className="flex flex-col gap-2 text-sm text-foreground-950/80">
                  <li className="flex justify-between">
                    <span>Mon &ndash; Fri</span>
                    <span className="text-foreground-950">09:00 &ndash; 20:00</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Saturday</span>
                    <span className="text-foreground-950">10:00 &ndash; 15:00</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Sunday</span>
                    <span className="italic text-foreground-950/55">Closed &mdash; rest is part of the practice</span>
                  </li>
                </ul>
              </div>

              {/* Getting here */}
              <div className="rounded-2xl border border-foreground-950/10 bg-background-50 p-6 md:p-7">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
                    <i className="ri-train-line text-lg"></i>
                  </span>
                  <span
                    className="uppercase font-medium text-foreground-950/60"
                    style={{ fontSize: "10px", letterSpacing: "0.3em" }}
                  >
                    Getting here
                  </span>
                </div>
                <ul className="flex flex-col gap-2 text-sm text-foreground-950/80">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0"></span>
                    <span>
                      <strong className="text-foreground-950 font-medium">Metro:</strong> Baixa-Chiado, 3 min walk.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0"></span>
                    <span>
                      <strong className="text-foreground-950 font-medium">Tram:</strong> Route 28, stop &ldquo;Chiado.&rdquo;
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0"></span>
                    <span>
                      <strong className="text-foreground-950 font-medium">Parking:</strong> Chiado public garage, 4 min walk.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}